/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { SafeUser } from '../../constants/users';
import { SurveyActionData } from '../../model/surveyData';
import { UniversalTrackerValuePlain } from '../../types/surveyScope';
import { InitiativePermissions } from './InitiativePermissions';
import { SurveyPermissions } from './SurveyPermissions';

type PermissionsUser = Pick<SafeUser, '_id' | 'permissions'>;
type PermissionsUtrv = Pick<UniversalTrackerValuePlain, '_id' | 'stakeholders' | 'compositeData' | 'initiativeId'>;
type PermissionsSurvey = Pick<SurveyActionData, '_id' | 'roles' | 'stakeholders' | 'initiativeId'>;

export class UtrvPermissions {

  public static canAccess(utrv: PermissionsUtrv, survey: PermissionsSurvey | undefined, user: PermissionsUser): boolean {
    const userId = user._id;
    const { stakeholder, verifier } = utrv.stakeholders;
    if (stakeholder.includes(userId) || verifier.includes(userId)) {
      return true;
    }

    const surveyId = utrv.compositeData?.surveyId;
    if (survey && surveyId && survey._id === surveyId) {
      return SurveyPermissions.canAccess(survey, user);
    }

    const initiativeId = utrv.initiativeId;
    return InitiativePermissions.canAccess(user, initiativeId);
  }

  public static canAccessComments(utrv: PermissionsUtrv, survey: PermissionsSurvey | undefined, user: PermissionsUser): boolean {
    const hasAccess = this.canAccess(utrv, survey, user);
    if (hasAccess) {
      return true;
    }
    return this.canAssure(utrv, user);
  }

  public static canAssure(utrv: PermissionsUtrv, user: PermissionsUser): boolean {
    // @TODO
    return false;
  }

  public static canContribute(utrv: PermissionsUtrv, survey: PermissionsSurvey | undefined, user: PermissionsUser): boolean {
    const userId = user._id;
    const { stakeholder } = utrv.stakeholders;
    if (stakeholder.includes(userId)) {
      return true;
    }

    const surveyId = utrv.compositeData?.surveyId;
    if (survey && surveyId && survey._id === surveyId) {
      return SurveyPermissions.canContribute(survey, user);
    }

    const initiativeId = utrv.initiativeId;
    return InitiativePermissions.canContribute(user, initiativeId);
  }

  public static canVerify(utrv: PermissionsUtrv, survey: PermissionsSurvey | undefined, user: PermissionsUser): boolean {
    const userId = user._id;
    const { verifier } = utrv.stakeholders;
    if (verifier.includes(userId)) {
      return true;
    }

    const surveyId = utrv.compositeData?.surveyId;
    if (survey && surveyId && survey._id === surveyId) {
      return SurveyPermissions.canVerify(survey, user);
    }

    const initiativeId = utrv.initiativeId;
    return InitiativePermissions.canVerify(user, initiativeId);
  }

  public static canContributeBulk(utrvs: PermissionsUtrv[], survey: PermissionsSurvey, user: PermissionsUser): boolean {
    const userId = user._id;
    const surveyId = survey?._id
    const mismatchSurvey = []
    const utrvCheck = utrvs.every((utrv) => {
      const { stakeholder } = utrv.stakeholders;
      const utrvSurveyId = utrv.compositeData?.surveyId;
      if (surveyId && surveyId !== utrvSurveyId) {
        mismatchSurvey.push(utrv)
        return false
      }
      return stakeholder.includes(userId)
    })
    if (mismatchSurvey.length > 0) {
      return false
    }

    if (utrvCheck) {
      return true
    }

    return SurveyPermissions.canContribute(survey, user)
  }

  public static canVerifyBulk(utrvs: PermissionsUtrv[], survey: PermissionsSurvey, user: PermissionsUser): boolean {
    const userId = user._id;
    const surveyId = survey?._id
    const mismatchSurvey = []

    const utrvCheck = utrvs.every((utrv) => {
      const { verifier } = utrv.stakeholders;
      const utrvSurveyId = utrv.compositeData?.surveyId;
      if (surveyId && surveyId !== utrvSurveyId) {
        mismatchSurvey.push(utrv)
        return false
      }
      return verifier.includes(userId)
    })
    if (mismatchSurvey.length > 0) {
      return false
    }
    if (utrvCheck) {
      return true
    }

    return SurveyPermissions.canVerify(survey, user)
  }
}
